/**
 * El invariante de **un solo eje** de `ShareBar`, hecho cumplir en el call site.
 *
 * `ShareBar` documenta su contrato — «`cantidad / total × 100` debe dar `pct`» —
 * pero nada lo hacía cumplir: los tres call sites armaban `cantidad` y `pct` de
 * dos campos independientes del payload. Ya pasó una vez: la barra del split de
 * revenue recibía la **cantidad de clientes** con el **porcentaje del MRR** y la
 * leyenda decía «Enterprise 12 (76,1%)», una división que no da. Aquel bug se
 * cerró cambiando el campo, no la forma de armarlo, así que podía volver sin que
 * el compilador ni un test lo notaran.
 *
 * Acá el porcentaje **no se recibe: se deriva** de la misma cantidad que la
 * leyenda imprime al lado. Un segmento cuyo `pct` no cierre con su `cantidad`
 * deja de ser representable.
 *
 * Cuando el payload también publica su propio porcentaje (`pctMrr`,
 * `pctPortfolio`), ese campo queda como **cross-check**, no como fuente: ver
 * `divergenciaDePct`.
 */
import type { ShareSegment } from '../../ui'

/** Un segmento antes de derivarle el porcentaje: solo el eje que se imprime. */
export interface EntradaDeEje {
  id: string
  label: string
  /** En la unidad de la barra. `null` = sin dato; no aporta ancho. */
  cantidad: number | null
  severidad: ShareSegment['severidad']
  /** Rayado diagonal, para el segundo segmento que comparte color. */
  trama?: boolean
}

/**
 * Segmentos de `ShareBar` con el porcentaje derivado de `cantidad / total`.
 *
 * @param total El mismo `total` que se le pasa a `<ShareBar>`. Si no lo fuera,
 *   los anchos no sumarían el 100% y la leyenda no se podría recalcular.
 */
export function segmentosDeUnEje(
  entradas: readonly EntradaDeEje[],
  total: number,
): ShareSegment[] {
  return entradas.map((entrada) => ({
    ...entrada,
    // Sin dato no se dibuja ancho: un cero afirmaría que el segmento no aporta
    // nada, y lo que pasa es que no se sabe.
    pct:
      total === 0 || entrada.cantidad === null
        ? 0
        : (entrada.cantidad / total) * 100,
  }))
}

/**
 * Diferencia en puntos entre el porcentaje derivado y el que publica el payload.
 * Sirve de cross-check en tests: si el servidor y la pantalla no coinciden, uno
 * de los dos está calculando sobre otro total.
 */
export function divergenciaDePct(
  segmento: Pick<ShareSegment, 'pct'>,
  pctDelPayload: number | null,
): number | null {
  if (pctDelPayload === null) return null
  return Math.abs(segmento.pct - pctDelPayload)
}
